import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Clock, Loader2, Copy, AlertCircle, ArrowLeft } from 'lucide-react';
import { shiftApi, settingsApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Alert, AlertDescription } from '../ui/alert';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

interface ShiftTemplateData {
  id: string;
  name: string;
  description?: string;
  start_time: string;
  end_time: string;
  duration_hours: number;
  color?: string;
  is_active: boolean;
  rest_days?: string[];
}

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ShiftTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<ShiftTemplateData | null>(null);
  const [viewDetails, setViewDetails] = useState<ShiftTemplateData | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    start_time: string;
    end_time: string;
    duration_hours: number;
    color: string;
    is_active: boolean;
    rest_days: string[];
  }>({
    name: '',
    description: '',
    start_time: '09:00',
    end_time: '17:00',
    duration_hours: 8,
    color: '#3B82F6',
    is_active: true,
    rest_days: ['Sat', 'Sun'],
  });

  // Auto-calculate duration when start/end times change
  useEffect(() => {
    if (formData.start_time && formData.end_time) {
      const duration = calculateDuration(formData.start_time, formData.end_time);
      if (duration !== formData.duration_hours) {
        setFormData(prev => ({ ...prev, duration_hours: duration }));
      }
    }
  }, [formData.start_time, formData.end_time]);

  const calculateDuration = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    // Handle overnight shifts
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }
    
    const durationHours = (endMinutes - startMinutes) / 60;
    return Math.round(durationHours * 10) / 10; // Round to 1 decimal
  };

  const { data: shifts = [], isLoading } = useQuery<ShiftTemplateData[]>({
    queryKey: ['shifts'],
    queryFn: async () => {
      const data = await shiftApi.shifts.getAll();
      return data as ShiftTemplateData[];
    },
  });

  // Fetch shift planning settings for validation
  const { data: shiftSettings } = useQuery({
    queryKey: ['shiftPlanningSettings'],
    queryFn: () => settingsApi.shiftPlanning.get(),
  });

  const minDuration = shiftSettings?.minShiftDuration ?? 4;
  const maxDuration = shiftSettings?.maxShiftDuration ?? 12;

  const createMutation = useMutation({
    mutationFn: shiftApi.shifts.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      setIsCreateOpen(false);
      resetForm();
      toast({ title: 'Shift template created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => shiftApi.shifts.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      setEditingShift(null);
      resetForm();
      toast({ title: 'Shift template updated successfully' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: shiftApi.shifts.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      toast({ title: 'Shift template deleted successfully' });
    },
  });

  const handleCreate = () => {
    if (!formData.name || !formData.start_time || !formData.end_time) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    // Validate duration against settings
    if (formData.duration_hours < minDuration) {
      toast({ 
        title: 'Invalid Shift Duration', 
        description: `Shift duration (${formData.duration_hours}h) is less than the minimum allowed duration (${minDuration}h). Please adjust the start or end time.`,
        variant: 'destructive' 
      });
      return;
    }

    if (formData.duration_hours > maxDuration) {
      toast({ 
        title: 'Invalid Shift Duration', 
        description: `Shift duration (${formData.duration_hours}h) exceeds the maximum allowed duration (${maxDuration}h). Please adjust the start or end time.`,
        variant: 'destructive' 
      });
      return;
    }

    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!editingShift) return;

    // Validate duration against settings
    if (formData.duration_hours < minDuration) {
      toast({ 
        title: 'Invalid Shift Duration', 
        description: `Shift duration (${formData.duration_hours}h) is less than the minimum allowed duration (${minDuration}h). Please adjust the start or end time.`,
        variant: 'destructive' 
      });
      return;
    }

    if (formData.duration_hours > maxDuration) {
      toast({ 
        title: 'Invalid Shift Duration', 
        description: `Shift duration (${formData.duration_hours}h) exceeds the maximum allowed duration (${maxDuration}h). Please adjust the start or end time.`,
        variant: 'destructive' 
      });
      return;
    }

    updateMutation.mutate({ id: editingShift.id, data: formData });
  };

  const handleEdit = (shift: ShiftTemplateData) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      description: shift.description || '',
      start_time: shift.start_time?.slice(0, 5) || '09:00',
      end_time: shift.end_time?.slice(0, 5) || '17:00',
      duration_hours: shift.duration_hours || calculateDuration(shift.start_time, shift.end_time),
      color: shift.color || '#3B82F6',
      is_active: shift.is_active ?? true,
      rest_days: shift.rest_days || ['Sat', 'Sun'],
    });
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Delete shift template "${name}" ? `)) {
      deleteMutation.mutate(id);
    }
  };

  const toggleRestDay = (day: string) => {
    setFormData(prev => ({
      ...prev,
      rest_days: prev.rest_days.includes(day)
        ? prev.rest_days.filter(d => d !== day)
        : [...prev.rest_days, day]
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      start_time: '09:00',
      end_time: '17:00',
      duration_hours: 8,
      color: '#3B82F6',
      is_active: true,
      rest_days: ['Sat', 'Sun']
    });
  };

  const getShiftType = (start: string) => {
    if (!start) return 'Regular';
    const hour = parseInt(start.split(':')[0]);
    if (hour >= 5 && hour < 12) return 'Morning';
    if (hour >= 12 && hour < 17) return 'Afternoon';
    if (hour >= 17 || hour < 5) return 'Night';
    return 'Regular';
  };

  const formatTime = (time: string) => time?.slice(0, 5) || 'N/A';

  const formatRestDays = (restDays: string[] | undefined) => {
    if (!restDays || restDays.length === 0) return 'None';
    return restDays.join(', ');
  };

  const getStatusBadge = (status: string) => {
    const isActive = status.toUpperCase() === 'ACTIVE';
    const classNames = isActive 
      ? 'bg-emerald-50 text-emerald-705 border-emerald-250/50 dark:bg-emerald-950/20 dark:text-emerald-400' 
      : 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", classNames)} variant="outline">
        {status.toLowerCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-650" />
        <span className="ml-2 mt-3 font-bold text-xs text-slate-400">Loading shift templates...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-3.5">
          <Button
            onClick={() => navigate('/shift-planning')}
            variant="outline"
            size="icon"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Create and manage reusable shift templates</h2>
          </div>
        </div>
        <Button 
          onClick={() => setIsCreateOpen(true)} 
        >
          <Plus className="h-4 w-4 shrink-0 mr-1.5" />
          <span>New Template</span>
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Total Templates</p>
                <p className="text-3xl font-bold text-[#143A62] dark:text-slate-100 mt-2">{shifts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center shrink-0 shadow-sm border border-blue-100/30">
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Active</p>
                <p className="text-3xl font-bold text-[#143A62] dark:text-slate-100 mt-2">{shifts.filter((s) => s.is_active).length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center shrink-0 shadow-sm border border-emerald-100/30">
                <Clock className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-105 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="pt-6 px-6 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400 tracking-wider">Inactive</p>
                <p className="text-3xl font-bold text-[#143A62] dark:text-slate-100 mt-2">{shifts.filter((s) => !s.is_active).length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800/40 flex items-center justify-center shrink-0 shadow-sm border border-slate-200/30">
                <Clock className="h-6 w-6 text-slate-500 dark:text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates Table Card */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Templates</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto custom-scrollbar">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-150 dark:border-slate-800">
                <TableHead className="p-4 bg-slate-50/50 dark:bg-slate-800/20 font-bold text-xs text-slate-400 tracking-wider">Template</TableHead>
                <TableHead className="p-4 bg-slate-50/50 dark:bg-slate-800/20 font-bold text-xs text-slate-400 tracking-wider">Time</TableHead>
                <TableHead className="p-4 bg-slate-50/50 dark:bg-slate-800/20 font-bold text-xs text-slate-400 tracking-wider">Duration</TableHead>
                <TableHead className="p-4 bg-slate-50/50 dark:bg-slate-800/20 font-bold text-xs text-slate-400 tracking-wider">Rest Days</TableHead>
                <TableHead className="p-4 bg-slate-50/50 dark:bg-slate-800/20 font-bold text-xs text-slate-400 tracking-wider">Status</TableHead>
                <TableHead className="p-4 bg-slate-50/50 dark:bg-slate-800/20 font-bold text-xs text-slate-400 tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shifts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-slate-400">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300 dark:text-slate-700 animate-pulse" />
                    <p className="font-bold text-sm text-[#143A62] dark:text-slate-200">No shift templates found</p>
                    <p className="text-xs font-semibold text-slate-400 mt-2">Create your first shift template</p>
                  </TableCell>
                </TableRow>
              ) : (
                shifts.map((shift) => (
                  <TableRow 
                    key={shift.id} 
                    className="cursor-pointer hover:bg-slate-50/30 dark:hover:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800 transition-colors" 
                    onClick={() => setViewDetails(shift)}
                  >
                    <TableCell className="p-4">
                      <div className="font-bold text-xs text-[#143A62] dark:text-slate-200 flex items-center space-x-2.5">
                        <div 
                          className="w-4 h-4 rounded-md border border-slate-200/50 shadow-sm shrink-0" 
                          style={{ backgroundColor: shift.color || '#3B82F6' }}
                        ></div>
                        <span>{shift.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="p-4">
                      <span className="text-xs font-semibold text-slate-500">
                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </span>
                    </TableCell>
                    <TableCell className="p-4">
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                        {shift.duration_hours || calculateDuration(shift.start_time, shift.end_time)} hrs
                      </span>
                    </TableCell>
                    <TableCell className="p-4">
                      <span className="text-xs font-semibold text-slate-500">
                        {formatRestDays(shift.rest_days)}
                      </span>
                    </TableCell>
                    <TableCell className="p-4">
                      {getStatusBadge(shift.is_active ? 'Active' : 'Inactive')}
                    </TableCell>
                    <TableCell className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleEdit(shift)}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDelete(shift.id, shift.name)} 
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateOpen || !!editingShift} onOpenChange={() => { setIsCreateOpen(false); setEditingShift(null); resetForm(); }}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">{editingShift ? 'Edit' : 'Create'} Shift Template</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure a reusable shift template
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-bold text-slate-400 tracking-wider">Shift Name *</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                placeholder="e.g., Morning Shift" 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-bold text-slate-400 tracking-wider">Description</Label>
              <Textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                rows={2} 
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-xs shadow-sm"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="start_time" className="text-xs font-bold text-slate-400 tracking-wider">Start Time *</Label>
                <Input 
                  id="start_time" 
                  type="time" 
                  value={formData.start_time} 
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} 
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="end_time" className="text-xs font-bold text-slate-400 tracking-wider">End Time *</Label>
                <Input 
                  id="end_time" 
                  type="time" 
                  value={formData.end_time} 
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} 
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
                />
              </div>
            </div>
            
            <div className="space-y-2.5">
              <Label htmlFor="duration" className="text-xs font-bold text-slate-400 tracking-wider">Duration (auto-calculated)</Label>
              <div className="flex items-center space-x-2">
                <Input 
                  id="duration" 
                  type="number" 
                  step="0.5"
                  value={formData.duration_hours} 
                  onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) || 0 })}
                  className={cn(
                    "rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm bg-slate-50/50 dark:bg-slate-900/50",
                    (formData.duration_hours < minDuration || formData.duration_hours > maxDuration) && 'border-rose-500 focus-visible:ring-rose-500'
                  )}
                />
                <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">hours</span>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-slate-400 tracking-wide">Auto-calculated from start/end times. Override if needed.</p>
                {formData.duration_hours < minDuration && (
                  <Alert className="mt-2 border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:border-rose-900/30 dark:text-rose-400 rounded-xl p-3">
                    <AlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0" />
                    <AlertDescription className="text-xs font-bold">
                      Duration is below minimum ({minDuration}h). Shift cannot be saved.
                    </AlertDescription>
                  </Alert>
                )}
                {formData.duration_hours > maxDuration && (
                  <Alert className="mt-2 border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:border-rose-900/30 dark:text-rose-400 rounded-xl p-3">
                    <AlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0" />
                    <AlertDescription className="text-xs font-bold">
                      Duration exceeds maximum ({maxDuration}h). Shift cannot be saved.
                    </AlertDescription>
                  </Alert>
                )}
                {formData.duration_hours >= minDuration && formData.duration_hours <= maxDuration && (
                  <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 mt-1">✓ Duration is within allowed range ({minDuration}h - {maxDuration}h)</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="color" className="text-xs font-bold text-slate-400 tracking-wider">Color Indicator *</Label>
              <div className="flex items-center space-x-3.5">
                <div className="relative shrink-0 w-11 h-10 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                  <Input 
                    id="color" 
                    type="color" 
                    value={formData.color} 
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="absolute inset-0 w-full h-full p-0 border-0 cursor-pointer scale-[1.5]"
                  />
                </div>
                <div 
                  className="w-8 h-8 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm shrink-0" 
                  style={{ backgroundColor: formData.color }}
                ></div>
                <span className="text-xs font-bold text-slate-500 tracking-wide uppercase">{formData.color}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Rest Days *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div 
                    key={day}
                    onClick={() => toggleRestDay(day)}
                    className={cn(
                      "px-3 py-1.5 text-xs font-bold rounded-xl cursor-pointer border transition-all select-none",
                      formData.rest_days.includes(day)
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm dark:bg-indigo-500 dark:border-indigo-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800'
                    )}
                  >
                    {day}
                  </div>
                ))}
              </div>
              <p className="text-[10px] font-bold text-slate-400 mt-1">
                Selected: {formData.rest_days.length > 0 ? formData.rest_days.join(', ') : 'None'}
              </p>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setIsCreateOpen(false); setEditingShift(null); resetForm(); }}
            >
              Cancel
            </Button>
            <Button 
              onClick={editingShift ? handleUpdate : handleCreate} 
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingShift ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!viewDetails} onOpenChange={() => setViewDetails(null)}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0 animate-in fade-in zoom-in-95 duration-200">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Shift Template Details</DialogTitle>
          </DialogHeader>
          
          {viewDetails && (
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="flex items-center space-x-3 pb-3 border-b border-slate-50 dark:border-slate-800">
                <div 
                  className="w-5.5 h-5.5 rounded-lg border border-slate-200 shadow-sm shrink-0" 
                  style={{ backgroundColor: viewDetails.color || '#3B82F6' }}
                ></div>
                <h3 className="text-lg font-bold text-[#143A62] dark:text-slate-100">{viewDetails.name}</h3>
              </div>
              <div className="grid grid-cols-2 gap-6 bg-slate-50/50 dark:bg-slate-800/10 p-4.5 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div>
                  <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">Start Time</Label>
                  <p className="font-bold text-xs text-slate-700 mt-1 dark:text-slate-200">{formatTime(viewDetails.start_time)}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">End Time</Label>
                  <p className="font-bold text-xs text-slate-700 mt-1 dark:text-slate-200">{formatTime(viewDetails.end_time)}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">Duration</Label>
                  <p className="font-bold text-xs text-slate-700 mt-1 dark:text-slate-200">{viewDetails.duration_hours || calculateDuration(viewDetails.start_time, viewDetails.end_time)} hours</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(viewDetails.is_active ? 'Active' : 'Inactive')}
                  </div>
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">Rest Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {viewDetails.rest_days && viewDetails.rest_days.length > 0 ? (
                    viewDetails.rest_days.map((day) => (
                      <Badge key={day} variant="outline" className="px-2.5 py-0.5 rounded-lg border-slate-200 bg-white font-bold text-[10px] text-slate-600 shadow-none dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800">{day}</Badge>
                    ))
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">No rest days configured</span>
                  )}
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">Color</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <div 
                    className="w-5 h-5 rounded-lg border border-slate-200/50 shadow-sm" 
                    style={{ backgroundColor: viewDetails.color || '#3B82F6' }}
                  ></div>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{viewDetails.color || '#3B82F6'}</span>
                </div>
              </div>
              
              {viewDetails.description && (
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-bold text-slate-405 tracking-wider uppercase">Description</Label>
                  <p className="text-xs font-semibold text-slate-500 leading-relaxed bg-slate-50/20 dark:bg-slate-800/5 p-3 rounded-xl border border-slate-100/50 dark:border-slate-800/30">{viewDetails.description}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setViewDetails(null)}>Close</Button>
            <Button 
              onClick={() => { setViewDetails(null); handleEdit(viewDetails!); }}
            >
              <Edit className="h-3.5 w-3.5 shrink-0 mr-1.5" />
              <span>Edit</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftTemplates;
